import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
} from "react";
import {
  APPOINTMENT_STATUS_LABELS,
  APPOINTMENT_STATUS_ORDER,
  CATEGORY_COLORS,
  CLIENT_TYPE_LABELS,
  LEAD_SOURCE_LABELS,
  sumLines,
  vehicleLabel,
  type AppointmentStatus,
  type AppointmentView,
  type CategoryWithServices,
  type Client,
  type DiscountRule,
  type ClientType,
  type LeadSource,
  type Service,
  type Technician,
  type Vehicle,
} from "@shared/modules/x-atelier";
import {
  SHOP_TIME_ZONE,
  internalBlocksOverlapping,
  serviceEndMs,
  type AgendaRules,
  type InternalBlock,
} from "@shared/modules/x-site";
import { ApiError } from "../../../core/lib/api";
import { moduleEnabled } from "../../../core/blueprint";
import { Button } from "../../../core/components/ui/button";
import { Icon } from "../../../core/components/ui/icon";
import { Field, Input, Select, Textarea } from "../../../core/components/ui/input";
import { Modal } from "../../../core/components/ui/modal";
import { useToastStore } from "../../../core/stores/toast";
import {
  createAppointment,
  createAppointmentFromRequest,
  createClient,
  createVehicle,
  getAgendaRules,
  getAppointment,
  getCatalogue,
  getClient,
  getFrequentServices,
  listClients,
  listDiscountRules,
  listTechnicians,
  updateAppointment,
  type AppointmentInput,
  type ClientRow,
  type InternalBlockDetails,
} from "../lib/api";
import { addMinutesLocal, dateTimeLocalToIso, toDateTimeLocalValue } from "../lib/dates";
import { centsToInput, parseAmountToCents } from "../lib/money";
import { isInactive } from "../lib/status";
import {
  LinesEditor,
  draftFromLine,
  draftTotals,
  lineFromService,
  linesToInput,
  type DraftLine,
} from "./lines-editor";
import { SlotPicker } from "./slot-picker";
import {
  VehicleFields,
  vehicleFormFrom,
  vehicleInputFrom,
  type VehicleFormState,
} from "./vehicle-form-modal";

const FORM_ID = "atelier-appointment-form";
const MIN_DURATION = 30;

/** Ce qu'il faut savoir d'un client pour l'afficher dans la modal. */
type PickedClient = Pick<Client, "id" | "name" | "phone">;

export interface AppointmentPreset {
  client?: PickedClient;
  /** Client à créer, coordonnées connues (demande du site) : la création rapide s'ouvre préremplie. */
  newClient?: { name: string; phone: string | null; email: string | null };
  vehicleId?: string;
  /** Véhicule à créer, renseignements connus (année, marque, modèle, catégorie, plaque). */
  newVehicle?: Partial<VehicleFormState>;
  /** Début proposé (ex. clic sur un jour de l'agenda). */
  startAt?: Date;
  /** Services du catalogue à cocher d'emblée, avec leur quantité. */
  lines?: Array<{ serviceId: string; quantity: number }>;
  technicianId?: string;
  source?: LeadSource;
  notes?: string;
  /** Préférence de date du client, rappelée dans le sélecteur de créneaux. */
  preference?: string | null;
  /** Jour et moment choisis par le client sur le site : le sélecteur s'y ouvre. */
  preferredDate?: string | null;
  preferredPeriod?: "matin" | "apres-midi" | null;
  /** Demande du site d'origine : l'enregistrement crée le rendez-vous et l'y lie, sans doublon. */
  requestId?: string;
  /** Ouvrir le sélecteur de créneaux disponibles dès l'ouverture. */
  openSlots?: boolean;
}

interface AppointmentModalProps {
  open: boolean;
  /** Présent : édition ; absent : création. */
  appointment?: AppointmentView;
  preset?: AppointmentPreset;
  onClose: () => void;
  onSaved: (appointment: AppointmentView) => void;
}

/** Début par défaut : demain 8 h, ou la date proposée à 8 h si elle n'a pas d'heure. */
function defaultStart(preset?: AppointmentPreset): string {
  const base = preset?.startAt ? new Date(preset.startAt) : new Date(Date.now() + 86_400_000);
  if (!preset?.startAt || (base.getHours() === 0 && base.getMinutes() === 0))
    base.setHours(8, 0, 0, 0);
  return toDateTimeLocalValue(base.getTime());
}

/** Fin d'après les services : début + Σ durées, pause de midi comprise pour un soin long. */
function servicesEndMs(startMs: number, durationMin: number, rules: AgendaRules | null): number {
  const work = Math.max(MIN_DURATION, durationMin);
  return rules ? serviceEndMs(startMs, work, rules, rules.timeZone) : startMs + work * 60_000;
}

/**
 * La fin enregistrée correspond-elle encore à la fin calculée d'après les
 * services ? Si oui, elle n'a pas été fixée à la main : modifier les services
 * la recalcule, au lieu d'obliger à ressaisir l'heure (demande de l'atelier).
 */
function followsServices(a: AppointmentView, rules: AgendaRules | null): boolean {
  const start = new Date(a.startAt).getTime();
  const end = new Date(a.endAt).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end)) return false;
  // Une minute de tolérance : les imports arrondissent parfois les secondes.
  return Math.abs(end - servicesEndMs(start, sumLines(a.lines).durationMin, rules)) < 60_000;
}

const hourFormat = new Intl.DateTimeFormat("fr-CA", {
  timeZone: SHOP_TIME_ZONE,
  hour: "numeric",
  minute: "2-digit",
  hourCycle: "h23",
});

/** « 12 h » / « 12 h 30 », à l'heure de l'atelier. */
function hourLabel(ms: number): string {
  const [h, m] = hourFormat.format(new Date(ms)).split(/\D+/).map(Number);
  return m ? `${h} h ${String(m).padStart(2, "0")}` : `${h} h`;
}

/** « la pause de midi (12 h – 13 h) », « la fermeture Inventaire (8 h – 17 h) ». */
function describeBlocks(blocks: InternalBlock[]): string {
  return blocks
    .map(
      (b) =>
        `${b.kind === "pause" ? "la pause de midi" : `la fermeture « ${b.label} »`} (${hourLabel(b.start)} – ${hourLabel(b.end)})`,
    )
    .join(" et ");
}

/**
 * Modal « Nouveau rendez-vous » / « Modifier le rendez-vous », réutilisée par
 * l'agenda, les travaux et la fiche client : client (recherche + création
 * rapide), véhicule du client ou nouveau, services cochés → lignes, technicien,
 * plage horaire (fin calculée par `sumLines` mais modifiable), statut, source,
 * notes et acompte.
 */
export function AppointmentModal({
  open,
  appointment,
  preset,
  onClose,
  onSaved,
}: AppointmentModalProps) {
  const push = useToastStore((s) => s.push);

  // Références (chargées à l'ouverture).
  const [catalogue, setCatalogue] = useState<CategoryWithServices[]>([]);
  const [frequent, setFrequent] = useState<Service[]>([]);
  const [rules, setRules] = useState<DiscountRule[]>([]);
  const [technicians, setTechnicians] = useState<Technician[]>([]);

  // Client.
  const [client, setClient] = useState<PickedClient | null>(null);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<ClientRow[]>([]);
  const [searching, setSearching] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);
  const [quick, setQuick] = useState({
    name: "",
    phone: "",
    email: "",
    type: "particulier" as ClientType,
  });
  const [quickSaving, setQuickSaving] = useState(false);

  // Véhicule : « » = aucun, « new » = à créer, sinon l'identifiant.
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [vehicleId, setVehicleId] = useState("");
  const [newVehicle, setNewVehicle] = useState<VehicleFormState>(() => vehicleFormFrom());

  // Rendez-vous.
  const [lines, setLines] = useState<DraftLine[]>([]);
  const [technicianId, setTechnicianId] = useState("");
  const [startLocal, setStartLocal] = useState("");
  const [endLocal, setEndLocal] = useState("");
  // Tant que la fin n'a pas été touchée à la main, elle suit début + Σ durées.
  const [endTouched, setEndTouched] = useState(false);
  // Fin saisie à la main pendant cette ouverture : jamais réévaluée ensuite.
  const endEdited = useRef(false);
  const [status, setStatus] = useState<AppointmentStatus>("reserve");
  const [source, setSource] = useState<LeadSource>("appel_direct");
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [clientInstructions, setClientInstructions] = useState("");
  // Règles d'agenda (pause de midi, fermetures) et dérogation explicite.
  const [agendaRules, setAgendaRules] = useState<AgendaRules | null>(null);
  const [allowBlock, setAllowBlock] = useState(false);
  // Rendez-vous déplacé : prévenir le client du nouvel horaire (texto).
  const [notifyClient, setNotifyClient] = useState(true);
  const [serverBlock, setServerBlock] = useState<string | null>(null);
  const [slotsOpen, setSlotsOpen] = useState(false);
  const [presetLinesPending, setPresetLinesPending] = useState(false);
  // Couleur forcée ; vide = celle de la catégorie du premier service.
  const [color, setColor] = useState("");
  const [deposit, setDeposit] = useState("");
  const [paid, setPaid] = useState("");
  const [saving, setSaving] = useState(false);

  // Réinitialisation à chaque ouverture : la fenêtre reste montée entre deux usages.
  useEffect(() => {
    if (!open) return;
    if (appointment) {
      setClient({
        id: appointment.clientId,
        name: appointment.clientName,
        phone: appointment.clientPhone,
      });
      setVehicleId(appointment.vehicleId ?? "");
      setLines(appointment.lines.map(draftFromLine));
      setTechnicianId(appointment.technicianId ?? "");
      const start = appointment.schedulePending ? "" : toDateTimeLocalValue(appointment.startAt);
      setStartLocal(start);
      setEndLocal(appointment.schedulePending ? "" : toDateTimeLocalValue(appointment.endAt));
      // La fin ne se fige que si elle diffère déjà des durées du catalogue :
      // sinon elle continue de suivre les services, y compris en modification.
      setEndTouched(!start || !followsServices(appointment, agendaRules));
      endEdited.current = false;
      setStatus(appointment.status);
      setSource(appointment.source);
      setTitle(appointment.title);
      setNotes(appointment.notes ?? "");
      setClientInstructions(appointment.clientInstructions ?? "");
      setColor(appointment.color ?? "");
      setDeposit(centsToInput(appointment.depositCents));
      setPaid(centsToInput(appointment.paidCents));
    } else {
      setClient(preset?.client ?? null);
      setVehicleId(preset?.vehicleId ?? (preset?.newVehicle ? "new" : ""));
      setLines([]);
      setPresetLinesPending(Boolean(preset?.lines?.length));
      setTechnicianId(preset?.technicianId ?? "");
      const start = defaultStart(preset);
      setStartLocal(start);
      setEndLocal(addMinutesLocal(start, MIN_DURATION));
      setEndTouched(false);
      endEdited.current = false;
      setStatus("reserve");
      setSource(preset?.source ?? "appel_direct");
      setTitle("");
      setNotes(preset?.notes ?? "");
      setClientInstructions("");
      setColor("");
      setDeposit("");
      setPaid("");
    }
    setQuery("");
    setResults([]);
    const newClient = !appointment && !preset?.client ? preset?.newClient : undefined;
    setQuickOpen(Boolean(newClient));
    setQuick({
      name: newClient?.name ?? "",
      phone: newClient?.phone ?? "",
      email: newClient?.email ?? "",
      type: "particulier",
    });
    setNewVehicle({ ...vehicleFormFrom(), ...(appointment ? {} : (preset?.newVehicle ?? {})) });
    setAllowBlock(false);
    setNotifyClient(true);
    setServerBlock(null);
    setSlotsOpen(Boolean(!appointment && preset?.openSlots));
    setSaving(false);
  }, [open, appointment, preset]);

  useEffect(() => {
    if (!open) return;
    getCatalogue()
      .then(setCatalogue)
      .catch(() => setCatalogue([]));
    getFrequentServices()
      .then(setFrequent)
      .catch(() => setFrequent([]));
    listTechnicians()
      .then((list) => setTechnicians(list.filter((t) => t.active)))
      .catch(() => setTechnicians([]));
    listDiscountRules()
      .then(setRules)
      .catch(() => setRules([]));
    if (moduleEnabled("x-site"))
      getAgendaRules()
        .then(setAgendaRules)
        .catch(() => setAgendaRules(null));
  }, [open]);

  // Services prévus par la demande : cochés dès que le catalogue est là.
  useEffect(() => {
    if (!presetLinesPending || catalogue.length === 0) return;
    const services = catalogue.flatMap((c) => c.services);
    setLines(
      (preset?.lines ?? []).flatMap((l) => {
        const s = services.find((x) => x.id === l.serviceId);
        return s ? [{ ...lineFromService(s), quantity: Math.max(1, l.quantity) }] : [];
      }),
    );
    setPresetLinesPending(false);
  }, [presetLinesPending, catalogue, preset]);

  // Les véhicules suivent le client choisi.
  useEffect(() => {
    if (!open || !client) {
      setVehicles([]);
      return;
    }
    let cancelled = false;
    getClient(client.id)
      .then((d) => {
        if (!cancelled) setVehicles(d.vehicles);
      })
      .catch(() => {
        if (!cancelled) setVehicles([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, client]);

  // Recherche de client, avec un léger délai pour ne pas interroger à chaque touche.
  useEffect(() => {
    if (!open || client) return;
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      return;
    }
    setSearching(true);
    const timer = setTimeout(() => {
      listClients(q)
        .then((rows) => setResults(rows.slice(0, 8)))
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 250);
    return () => clearTimeout(timer);
  }, [open, client, query]);

  const totals = useMemo(() => draftTotals(lines), [lines]);

  // Services prévus à l'ouverture (rendez-vous modifié, demande du site) : en
  // tête du sélecteur, pour les retirer ou les remplacer sans les chercher.
  const priorityIds = useMemo(
    () =>
      (appointment?.lines ?? preset?.lines ?? []).flatMap((l) =>
        l.serviceId ? [l.serviceId] : [],
      ),
    [appointment, preset],
  );

  // Fin calculée : début + Σ durées (minimum 30 min), pause de midi comprise
  // pour un soin long, tant qu'elle n'a pas été modifiée à la main.
  useEffect(() => {
    if (endTouched || !startLocal) return;
    const from = dateTimeLocalToIso(startLocal);
    setEndLocal(
      agendaRules && from
        ? toDateTimeLocalValue(servicesEndMs(Date.parse(from), totals.durationMin, agendaRules))
        : addMinutesLocal(startLocal, Math.max(MIN_DURATION, totals.durationMin)),
    );
  }, [startLocal, totals.durationMin, endTouched, agendaRules]);

  // Les règles d'agenda arrivent après l'ouverture : la fin d'un rendez-vous
  // existant se juge alors pause comprise (soin long), sauf saisie à la main.
  useEffect(() => {
    if (!open || !appointment || !agendaRules || endEdited.current) return;
    if (appointment.schedulePending) return;
    setEndTouched(!followsServices(appointment, agendaRules));
  }, [open, appointment, agendaRules]);

  const pickClient = useCallback((c: PickedClient) => {
    setClient({ id: c.id, name: c.name, phone: c.phone });
    // Un véhicule en cours de saisie (demande du site) reste à créer pour ce client.
    setVehicleId((v) => (v === "new" ? "new" : ""));
    setQuery("");
    setResults([]);
  }, []);

  const clearClient = () => {
    setClient(null);
    setVehicleId("");
  };

  const quickCreate = async () => {
    if (!quick.name.trim() || quickSaving) return;
    setQuickSaving(true);
    try {
      const created = await createClient({
        name: quick.name.trim(),
        phone: quick.phone.trim() || null,
        email: quick.email.trim() || null,
        type: quick.type,
        source,
      });
      push("success", "Client créé", created.name);
      pickClient(created);
      setQuickOpen(false);
    } catch {
      push("danger", "Création du client impossible");
    } finally {
      setQuickSaving(false);
    }
  };

  const newVehicleInput = vehicleId === "new" ? vehicleInputFrom(newVehicle) : null;
  const linesInput = useMemo(() => linesToInput(lines), [lines]);
  const startIso = dateTimeLocalToIso(startLocal);
  const endIso = dateTimeLocalToIso(endLocal);
  const depositCents = parseAmountToCents(deposit);
  const paidCents = parseAmountToCents(paid);

  // Pause de midi et fermetures : la règle normale du site et de Léo. Au CRM,
  // la planifier exige une dérogation explicite — seulement quand l'horaire
  // change : un rendez-vous déjà planifié ainsi n'est jamais invalidé.
  const workMinutes = lines.length ? Math.max(MIN_DURATION, totals.durationMin) : undefined;
  const blocks = useMemo(() => {
    if (!agendaRules || !startIso || isInactive(status)) return [];
    const startMs = Date.parse(startIso);
    const endMs = endIso ? Date.parse(endIso) : startMs + MIN_DURATION * 60_000;
    // Un soin long qui enjambe toute la pause ne la chevauche pas : le travail s'y interrompt.
    return internalBlocksOverlapping(
      startMs,
      endMs,
      agendaRules,
      agendaRules.timeZone,
      workMinutes,
    );
  }, [agendaRules, startIso, endIso, status, workMinutes]);
  const lunchIncluded =
    !endTouched &&
    startIso !== null &&
    endIso !== null &&
    workMinutes !== undefined &&
    Date.parse(endIso) - Date.parse(startIso) > workMinutes * 60_000;
  const timesChanged =
    !appointment ||
    (startIso !== null && Date.parse(startIso) !== Date.parse(String(appointment.startAt))) ||
    (endIso !== null && Date.parse(endIso) !== Date.parse(String(appointment.endAt)));
  const needsOverride = (blocks.length > 0 && timesChanged) || serverBlock !== null;
  // Un rendez-vous réservé ou confirmé, à venir, dont le début change : le
  // client reçoit le nouvel horaire par texto, sauf s'il est déjà au courant.
  const moved =
    appointment !== null &&
    appointment !== undefined &&
    !appointment.schedulePending &&
    startIso !== null &&
    Date.parse(startIso) !== Date.parse(String(appointment.startAt)) &&
    (status === "reserve" || status === "confirme") &&
    Date.parse(startIso) > Date.now();

  const problem = !client
    ? "Choisissez un client."
    : vehicleId === "new" && !newVehicleInput
      ? "Marque et modèle du véhicule sont requis."
      : !linesInput
        ? "Chaque ligne doit avoir un libellé et un prix lisible."
        : !startIso
          ? "Indiquez la date et l'heure de début."
          : endIso && startIso && endIso <= startIso
            ? "La fin doit suivre le début."
            : depositCents === null || paidCents === null
              ? "Montant illisible (ex. 150,00)."
              : needsOverride && !allowBlock
                ? "Confirmez la dérogation à la pause de midi, ou choisissez un autre horaire."
                : null;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (problem || saving || !client || !linesInput || !startIso) return;
    setSaving(true);
    try {
      let finalVehicleId: string | null = vehicleId || null;
      if (vehicleId === "new" && newVehicleInput) {
        finalVehicleId = (await createVehicle(client.id, newVehicleInput)).id;
      }
      const payload: AppointmentInput = {
        clientId: client.id,
        vehicleId: finalVehicleId,
        technicianId: technicianId || null,
        status,
        startAt: startIso,
        endAt: endIso ?? undefined,
        title: title.trim() || undefined,
        notes: notes.trim() || null,
        source,
        depositCents: depositCents ?? 0,
        paidCents: paidCents ?? 0,
        color: color || null,
        lines: linesInput,
        clientInstructions: clientInstructions.trim() || null,
        allowInternalBlock: needsOverride && allowBlock ? true : undefined,
        notifyClient: moved ? notifyClient : undefined,
      };
      const saved = appointment
        ? await updateAppointment(appointment.id, payload)
        : preset?.requestId
          ? await createAppointmentFromRequest(preset.requestId, payload)
          : await createAppointment(payload);
      push("success", appointment ? "Rendez-vous mis à jour" : "Rendez-vous créé", saved.title);
      onSaved(saved);
    } catch (err) {
      const code = err instanceof ApiError ? err.code : "";
      if (code === "internal_block" && err instanceof ApiError) {
        // Les règles ont changé depuis l'ouverture : on demande la dérogation.
        const details = err.details as InternalBlockDetails | undefined;
        setServerBlock(details?.message ?? "Ce rendez-vous chevauche une indisponibilité interne.");
        setAllowBlock(false);
        return;
      }
      if (code === "already_linked" && err instanceof ApiError && preset?.requestId) {
        // Deux clics, deux employés : la demande a déjà son rendez-vous.
        const existing = (err.details as { appointmentId?: string } | undefined)?.appointmentId;
        push("info", "Cette demande a déjà un rendez-vous", "Ouverture du rendez-vous existant.");
        if (existing) {
          const view = await getAppointment(existing).catch(() => null);
          if (view) onSaved(view);
        }
        return;
      }
      push(
        "danger",
        appointment ? "Échec de la mise à jour" : "Échec de la création",
        code === "vehicle_client_mismatch"
          ? "Ce véhicule n'appartient pas à ce client."
          : "Vérifiez les champs saisis.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      title={
        appointment
          ? "Modifier le rendez-vous"
          : preset?.requestId
            ? "Rendez-vous depuis une demande du site"
            : "Nouveau rendez-vous"
      }
      onClose={onClose}
      size="lg"
      footer={
        <>
          {problem ? <span className="rdv-modal__problem">{problem}</span> : null}
          <Button type="button" variant="ghost" onClick={onClose} disabled={saving}>
            Annuler
          </Button>
          <Button
            type="submit"
            form={FORM_ID}
            variant="primary"
            disabled={saving || problem !== null}
          >
            {saving ? "Enregistrement…" : appointment ? "Enregistrer" : "Créer le rendez-vous"}
          </Button>
        </>
      }
    >
      <form id={FORM_ID} className="atelier-form rdv-modal" onSubmit={submit}>
        {/* ---- Client ---- */}
        <section className="form-section">
          <div className="form-section__head">
            <h3 className="form-section__title">Client</h3>
          </div>
          {client ? (
            <div className="rdv-modal__picked">
              <span className="rdv-modal__picked-name">{client.name}</span>
              {client.phone ? <span className="rdv-modal__picked-meta">{client.phone}</span> : null}
              <Button type="button" variant="ghost" size="sm" onClick={clearClient}>
                Changer
              </Button>
            </div>
          ) : quickOpen ? (
            <div className="rdv-modal__quick">
              <div className="form-section__grid">
                <Field label="Nom">
                  <Input
                    value={quick.name}
                    onChange={(e) => setQuick({ ...quick, name: e.target.value })}
                    autoFocus
                  />
                </Field>
                <Field label="Téléphone">
                  <Input
                    type="tel"
                    value={quick.phone}
                    onChange={(e) => setQuick({ ...quick, phone: e.target.value })}
                  />
                </Field>
                <Field label="Courriel">
                  <Input
                    type="email"
                    value={quick.email}
                    onChange={(e) => setQuick({ ...quick, email: e.target.value })}
                  />
                </Field>
                <Field label="Type">
                  <Select
                    value={quick.type}
                    onChange={(e) => setQuick({ ...quick, type: e.target.value as ClientType })}
                  >
                    {Object.entries(CLIENT_TYPE_LABELS).map(([v, l]) => (
                      <option key={v} value={v}>
                        {l}
                      </option>
                    ))}
                  </Select>
                </Field>
              </div>
              <div className="rdv-modal__quick-actions">
                <Button type="button" variant="ghost" size="sm" onClick={() => setQuickOpen(false)}>
                  Retour à la recherche
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  disabled={!quick.name.trim() || quickSaving}
                  onClick={() => void quickCreate()}
                >
                  {quickSaving ? "Création…" : "Créer le client"}
                </Button>
              </div>
            </div>
          ) : (
            <div className="rdv-modal__search">
              <Field
                label="Rechercher un client"
                hint="Nom, téléphone, courriel, immatriculation ou NIV."
              >
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                    // Entrée choisit le premier client trouvé ; elle ne soumet jamais le rendez-vous.
                    if (e.key !== "Enter") return;
                    e.preventDefault();
                    if (results[0]) pickClient(results[0]);
                  }}
                  placeholder="ex : Tremblay, 418 555…"
                  autoFocus
                  autoComplete="off"
                />
              </Field>
              {results.length > 0 ? (
                <ul className="rdv-modal__results" aria-label="Clients trouvés">
                  {results.map((r) => (
                    <li key={r.id}>
                      <button
                        type="button"
                        className="rdv-modal__result"
                        onClick={() => pickClient(r)}
                      >
                        <span className="rdv-modal__result-name">{r.name}</span>
                        <span className="rdv-modal__result-meta">
                          {[
                            CLIENT_TYPE_LABELS[r.type],
                            r.phone,
                            `${r.vehicleCount} véhicule${r.vehicleCount > 1 ? "s" : ""}`,
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : query.trim().length >= 2 && !searching ? (
                <p className="rdv-modal__hint">Aucun client ne correspond.</p>
              ) : null}
              <div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  icon={<Icon name="plus" size={14} />}
                  onClick={() => {
                    setQuick({ name: query.trim(), phone: "", email: "", type: "particulier" });
                    setQuickOpen(true);
                  }}
                >
                  Créer un client
                </Button>
              </div>
            </div>
          )}
        </section>

        {/* ---- Véhicule ---- */}
        <section className="form-section">
          <div className="form-section__head">
            <h3 className="form-section__title">Véhicule</h3>
          </div>
          <div className="form-section__grid">
            <Field label="Véhicule du client">
              <Select
                value={vehicleId}
                onChange={(e) => setVehicleId(e.target.value)}
                disabled={!client}
              >
                <option value="">Aucun véhicule</option>
                {vehicles.map((v) => (
                  <option key={v.id} value={v.id}>
                    {vehicleLabel(v)}
                    {v.plate ? ` — ${v.plate}` : ""}
                  </option>
                ))}
                <option value="new">+ Nouveau véhicule</option>
              </Select>
            </Field>
          </div>
          {vehicleId === "new" ? (
            <VehicleFields form={newVehicle} onChange={setNewVehicle} autoFocus />
          ) : null}
        </section>

        {/* ---- Services ---- */}
        <section className="form-section">
          <div className="form-section__head">
            <h3 className="form-section__title">Services</h3>
            <p className="form-section__hint">
              Cherchez un service ou choisissez sa variante ; prix et durée restent modifiables par
              ligne.
            </p>
          </div>
          <LinesEditor
            vehicleClass={
              vehicleId === "new"
                ? newVehicleInput?.pricingClass
                : vehicles.find((v) => v.id === vehicleId)?.pricingClass
            }
            lines={lines}
            onChange={setLines}
            catalogue={catalogue}
            rules={rules}
            frequent={frequent}
            priorityIds={priorityIds}
          />
        </section>

        {/* ---- Planification ---- */}
        <section className="form-section">
          <div className="form-section__head">
            <h3 className="form-section__title">Planification</h3>
            {agendaRules ? (
              <Button
                type="button"
                size="sm"
                variant={slotsOpen ? "ghost" : "primary"}
                icon={<Icon name="calendar" size={14} />}
                aria-expanded={slotsOpen}
                onClick={() => setSlotsOpen((v) => !v)}
              >
                {slotsOpen ? "Masquer les créneaux" : "Créneaux disponibles"}
              </Button>
            ) : null}
          </div>
          {slotsOpen && agendaRules ? (
            <SlotPicker
              durationMin={Math.max(MIN_DURATION, totals.durationMin)}
              technicianId={technicianId || undefined}
              preference={appointment ? null : preset?.preference}
              preferredDate={appointment ? null : preset?.preferredDate}
              preferredPeriod={appointment ? null : preset?.preferredPeriod}
              selectedStart={startIso}
              onPick={(slot) => {
                setStartLocal(toDateTimeLocalValue(slot.startAt));
                setEndTouched(false);
                if (slot.technicianIds.length && !slot.technicianIds.includes(technicianId))
                  setTechnicianId(slot.technicianIds[0]!);
                setServerBlock(null);
              }}
            />
          ) : !appointment && preset?.preference ? (
            <p className="rdv-modal__hint">Préférence du client : {preset.preference}</p>
          ) : null}
          <div className="form-section__grid">
            <Field label="Technicien">
              <Select value={technicianId} onChange={(e) => setTechnicianId(e.target.value)}>
                <option value="">Non attribué</option>
                {technicians.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.displayName}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Début">
              <Input
                type="datetime-local"
                value={startLocal}
                onChange={(e) => setStartLocal(e.target.value)}
                required
              />
            </Field>
            <Field
              label="Fin"
              hint={
                endTouched
                  ? "Modifiée à la main."
                  : lunchIncluded
                    ? "Calculée d'après les durées des services, pause de midi comprise (soin long)."
                    : "Calculée d'après les durées des services."
              }
            >
              <Input
                type="datetime-local"
                value={endLocal}
                onChange={(e) => {
                  endEdited.current = true;
                  setEndTouched(true);
                  setEndLocal(e.target.value);
                }}
              />
            </Field>
            <Field label="Statut">
              <Select
                value={status}
                onChange={(e) => setStatus(e.target.value as AppointmentStatus)}
              >
                {APPOINTMENT_STATUS_ORDER.map((s) => (
                  <option key={s} value={s}>
                    {APPOINTMENT_STATUS_LABELS[s]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Source">
              <Select value={source} onChange={(e) => setSource(e.target.value as LeadSource)}>
                {Object.entries(LEAD_SOURCE_LABELS).map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Titre" hint="Vide : le véhicule, sinon le premier service.">
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="ex : 2023 Kia Seltos EX"
              />
            </Field>
          </div>
          {blocks.length > 0 || serverBlock ? (
            <div
              className={`rdv-modal__block${needsOverride ? " rdv-modal__block--warn" : ""}`}
              role="status"
            >
              <p className="rdv-modal__block-text">
                {serverBlock ??
                  `Ce rendez-vous chevauche ${describeBlocks(blocks)}. Le site et Léo ne proposent jamais ces créneaux.`}
              </p>
              {needsOverride ? (
                <label className="rdv-modal__override">
                  <input
                    type="checkbox"
                    checked={allowBlock}
                    onChange={(e) => setAllowBlock(e.target.checked)}
                  />
                  Planifier quand même (dérogation consignée à l’historique)
                </label>
              ) : (
                <p className="rdv-modal__block-text">
                  Horaire déjà planifié ainsi : rien à confirmer tant qu’il ne change pas.
                </p>
              )}
            </div>
          ) : null}
          {moved ? (
            <div className="rdv-modal__notify" role="status">
              <label className="rdv-modal__override">
                <input
                  type="checkbox"
                  checked={notifyClient}
                  onChange={(e) => setNotifyClient(e.target.checked)}
                />
                Prévenir le client du nouvel horaire par texto
              </label>
              <p className="rdv-modal__block-text">
                {notifyClient
                  ? "Envoyé deux minutes après l’enregistrement : un second déplacement le remplace. Décochez si le client est déjà au courant."
                  : "Aucun texto : le déplacement est noté à l’historique."}
              </p>
            </div>
          ) : null}
          <div className="form-section__grid form-section__grid--full">
            <Field
              label="Couleur dans l'agenda"
              hint="Par défaut, celle de la catégorie du premier service."
            >
              <ColorChoice value={color} onChange={setColor} />
            </Field>
          </div>
        </section>

        {/* ---- Paiement et notes ---- */}
        <section className="form-section">
          <div className="form-section__head">
            <h3 className="form-section__title">Paiement et notes</h3>
          </div>
          <div className="form-section__grid">
            <Field label="Acompte ($)">
              <Input
                inputMode="decimal"
                value={deposit}
                onChange={(e) => setDeposit(e.target.value)}
                placeholder="0,00"
              />
            </Field>
            {appointment ? (
              <Field label="Payé ($)">
                <Input
                  inputMode="decimal"
                  value={paid}
                  onChange={(e) => setPaid(e.target.value)}
                  placeholder="0,00"
                />
              </Field>
            ) : null}
          </div>
          <div className="form-section__grid form-section__grid--full">
            <Field label="Notes internes" hint="Jamais lues au client.">
              <Textarea
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="État du véhicule, demandes particulières, code de porte…"
              />
            </Field>
            <Field
              label="Consignes pour le client"
              hint="Lues au client par Léo s’il appelle au sujet de ce rendez-vous."
            >
              <Textarea
                rows={2}
                value={clientInstructions}
                onChange={(e) => setClientInstructions(e.target.value)}
                placeholder="ex : Arrivez 10 minutes avant ; laissez les clés au comptoir."
                maxLength={1000}
              />
            </Field>
          </div>
        </section>
      </form>
    </Modal>
  );
}

/**
 * Couleur du rendez-vous dans l'horaire : « Automatique » suit la catégorie du
 * premier service ; les pastilles forcent une couleur pour ce rendez-vous-là
 * (un véhicule de courtoisie, un retour de garantie…).
 */
function ColorChoice({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="color-choice" role="group" aria-label="Couleur du rendez-vous">
      <button
        type="button"
        className={`color-choice__auto${value === "" ? " is-on" : ""}`}
        aria-pressed={value === ""}
        onClick={() => onChange("")}
      >
        Automatique
      </button>
      {CATEGORY_COLORS.map((hex) => (
        <button
          key={hex}
          type="button"
          className={`color-choice__swatch${value === hex ? " is-on" : ""}`}
          style={{ background: hex }}
          aria-pressed={value === hex}
          aria-label={`Couleur ${hex}`}
          title={hex}
          onClick={() => onChange(hex)}
        />
      ))}
    </div>
  );
}
